// src/features/merchantConfig/components/editors/NumberEditor.jsx
//
// Number editor - uses an `inputmode="decimal"` text input rather than
// type="number" to avoid the browser's native up/down spinner (which is noisy
// in dense forms) and the silent NaN-on-paste behaviour. Commits the parsed
// Number on blur; refuses to commit non-finite values (lets the caller's
// validator surface the inline error).

import React, { useEffect, useState } from "react";
import { inputClass } from "./editorChrome";

/**
 * @param {{
 *   value: number | null | undefined,
 *   onCommit: (next: number) => void,
 *   onInvalid?: (msg: string) => void,
 *   ariaLabel: string,
 *   hasError?: boolean,
 *   inputId?: string,
 * }} props
 */
export default function NumberEditor({
  value,
  onCommit,
  onInvalid,
  ariaLabel,
  hasError,
  inputId,
}) {
  const [draft, setDraft] = useState(value === null || value === undefined ? "" : String(value));

  useEffect(() => {
    setDraft(value === null || value === undefined ? "" : String(value));
  }, [value]);

  const commit = () => {
    if (draft.trim() === "") {
      // Don't write empty string into a number slot - leaves the field as-is.
      if (onInvalid) onInvalid("Number cannot be empty.");
      return;
    }
    const n = Number(draft);
    if (!Number.isFinite(n)) {
      if (onInvalid) onInvalid("Enter a valid number.");
      return;
    }
    if (n !== value) onCommit(n);
  };

  return (
    <input
      id={inputId}
      type="text"
      inputMode="decimal"
      value={draft}
      aria-label={ariaLabel}
      aria-invalid={hasError || undefined}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
          e.currentTarget.blur();
        } else if (e.key === "Escape") {
          e.preventDefault();
          setDraft(value === null || value === undefined ? "" : String(value));
          e.currentTarget.blur();
        }
      }}
      className={inputClass({ hasError, extra: "text-right font-mono tabular-nums" })}
    />
  );
}
